// Visitor-type workflow contracts (module 07 remainders, ADR-027; PD-350 – PD-369): the optional
// `workflow` block of a visitor category, the policy's `restrictedArea` block, and the per-visit
// protocol, candidate, work-order and delivery records.
import { z } from 'zod';

const Uuid = z.uuid();
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const Label = z.object({ en: z.string().trim().min(1, 'v.required').max(80, 'v.tooLong'), ar: z.string().trim().max(80, 'v.tooLong').optional() });
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

// ------------------------------------------------------------------ policy blocks (versioned with the policy)

export const ProtocolStepRuleSchema = z.object({ key: Key, label: Label, required: z.boolean() });

/** PD-350: optional workflow behaviour of a visitor category. Configuration only, never a label branch. */
export const CategoryWorkflowSchema = z.object({
  protocol: z
    .object({
      contactUserId: Uuid.optional(),
      ownerUserId: Uuid.optional(),
      notify: z.boolean().optional(),
      assistedRoute: z.boolean().optional(),
      steps: z.array(ProtocolStepRuleSchema).max(15).optional(),
    })
    .optional(),
  candidate: z.object({ hrSponsorRequired: z.boolean() }).optional(),
  workOrder: z.object({ required: z.boolean(), permitRequired: z.boolean() }).optional(),
  delivery: z.object({ enabled: z.boolean() }).optional(),
});
export type CategoryWorkflowInput = z.infer<typeof CategoryWorkflowSchema>;

/** PD-364: what a restricted zone needs before it may appear in a credential scope. */
export const RestrictedAreaSchema = z.object({ identity: z.enum(['required', 'not_required']), escort: z.boolean() });
export type RestrictedAreaInput = z.infer<typeof RestrictedAreaSchema>;

// ------------------------------------------------------------------ commands

const Version = z.number().int().min(1);

/** Contact, owner, assisted route and the private itinerary. Omitted fields keep their value. */
export const UpdateProtocolRequest = z.object({
  expectedVersion: Version,
  contactUserId: Uuid.nullable().optional(),
  ownerUserId: Uuid.nullable().optional(),
  assistedRouteLocationId: Uuid.nullable().optional(),
  assistedRouteNote: optionalText(300),
  itinerary: optionalText(2000),
});
export const AssistedArrivalRequest = z.object({ expectedVersion: Version });
export const CompleteProtocolStepRequest = z
  .object({ outcome: z.enum(['completed', 'waived']), note: optionalText(500) })
  .superRefine((v, ctx) => {
    if (v.outcome === 'waived' && !v.note) ctx.addIssue({ code: 'custom', path: ['note'], message: 'vtw.v.waiveReason' });
  });
export const SetHrSponsorRequest = z.object({ expectedVersion: Version, userId: Uuid.nullable() });

export const CANDIDATE_NOTE_PURPOSE_VALUES = ['hiring_decision', 'interview_panel', 'candidate_request', 'complaint_or_audit'] as const;
export const CandidateNotesQuery = z.object({ purpose: z.enum(CANDIDATE_NOTE_PURPOSE_VALUES, { message: 'vtw.v.purposeRequired' }) });
export const AddCandidateNoteRequest = z.object({
  participationId: Uuid.nullable().optional(),
  body: z.string().trim().min(1, 'v.required').max(4000, 'v.tooLong'),
  correctsNoteId: Uuid.nullable().optional(),
});

export const BindWorkOrderRequest = z.object({ expectedVersion: z.number().int().min(0), workPackageId: Uuid, permitId: Uuid.nullable().optional() });

export const LinkDeliveryRequest = z.object({ participationId: Uuid, deliveryId: Uuid, visitVehicleId: Uuid.nullable().optional() });
export const CompleteDeliveryLinkRequest = z.object({ expectedVersion: Version });

// ------------------------------------------------------------------ responses

export interface PersonRef {
  readonly id: string;
  readonly displayName: string;
}

export interface ProtocolStepDto {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly required: boolean;
  readonly status: 'pending' | 'completed' | 'waived' | 'superseded';
  readonly policyVersion: number;
  readonly completedBy: PersonRef | null;
  readonly completedAt: string | null;
  readonly note: string | null;
}

export interface ProtocolDto {
  readonly contact: PersonRef | null;
  readonly owner: PersonRef | null;
  readonly notify: boolean;
  readonly assistedRouteRequired: boolean;
  readonly assistedRoute: { readonly location: { readonly id: string; readonly name: string } | null; readonly note: string | null; readonly assistedAt: string | null; readonly assistedBy: PersonRef | null };
  /** Null unless the caller is in the restricted audience (PD-352). */
  readonly itinerary: string | null;
  readonly itineraryVisible: boolean;
  readonly hasItinerary: boolean;
  readonly steps: readonly ProtocolStepDto[];
  readonly outstandingRequired: number;
  readonly can: { readonly manage: boolean; readonly editItinerary: boolean; readonly completeSteps: boolean; readonly recordAssisted: boolean };
}

export interface CandidateWorkflowDto {
  readonly hrSponsorRequired: boolean;
  readonly hrSponsor: PersonRef | null;
  readonly can: { readonly assignSponsor: boolean; readonly readNotes: boolean; readonly writeNotes: boolean };
}

export interface WorkBindingDto {
  readonly workPackage: { readonly id: string; readonly reference: string; readonly title: string; readonly startsOn: string; readonly endsOn: string; readonly status: string; readonly company: string };
  readonly permit: { readonly id: string; readonly reference: string; readonly typeCode: string; readonly status: string; readonly validFrom: string; readonly validUntil: string; readonly location: string } | null;
  readonly status: 'covered' | 'not_covered';
  readonly blockers: readonly string[];
  readonly evaluatedAt: string;
  readonly version: number;
}

export interface WorkEvaluationDto {
  readonly id: string;
  readonly trigger: 'bound' | 'submitted' | 'visit_changed' | 'participants_changed';
  readonly outcome: 'covered' | 'not_covered';
  readonly blockers: readonly string[];
  readonly visitEndsAt: string | null;
  readonly permitValidUntil: string | null;
  readonly liveCredentials: number;
  readonly evaluatedAt: string;
}

export interface WorkOrderDto {
  readonly required: boolean;
  readonly permitRequired: boolean;
  readonly binding: WorkBindingDto | null;
  readonly evaluations: readonly WorkEvaluationDto[];
  readonly can: { readonly bind: boolean };
}

export interface DeliveryLinkDto {
  readonly id: string;
  readonly participationId: string;
  readonly visitorName: string;
  readonly delivery: { readonly id: string; readonly reference: string; readonly courierCompany: string; readonly packageCount: number; readonly packageReference: string | null; readonly recipient: string | null; readonly handoverArea: string | null };
  /** The delivery domain's package outcome (pending, handed_over, refused). */
  readonly packageStatus: string;
  /** Recorded completion of this delivery participation; never a departure. */
  readonly completion: { readonly outcome: 'handed_over' | 'refused'; readonly at: string; readonly by: PersonRef } | null;
  /** Independent presence facts: the person's observed presence and the courier fact on the delivery. */
  readonly personPresence: string;
  readonly courierPresence: string;
  /** Vehicle presence from the expected vehicle (module 10) or the delivery's own vehicle fact; null = no vehicle. */
  readonly vehicle: { readonly plate: string; readonly presence: string } | null;
  readonly version: number;
}

export interface DeliveryWorkflowDto {
  readonly destinationRestricted: boolean;
  readonly links: readonly DeliveryLinkDto[];
  readonly can: { readonly link: boolean; readonly complete: boolean };
}

export interface VisitWorkflowResponse {
  readonly visitId: string;
  /** Version of the per-visit workflow record (0 before submission creates it). */
  readonly version: number;
  readonly policyVersion: number | null;
  readonly protocol: ProtocolDto | null;
  readonly candidate: CandidateWorkflowDto | null;
  readonly workOrder: WorkOrderDto | null;
  readonly delivery: DeliveryWorkflowDto | null;
}

export interface CandidateNoteDto {
  readonly id: string;
  readonly participationId: string | null;
  readonly author: PersonRef;
  readonly body: string;
  readonly correctsNoteId: string | null;
  readonly createdAt: string;
  readonly redacted: boolean;
}

export interface WorkOrderOptionsResponse {
  readonly workPackages: ReadonlyArray<{ readonly id: string; readonly reference: string; readonly title: string; readonly startsOn: string; readonly endsOn: string; readonly company: string }>;
  readonly permits: ReadonlyArray<{ readonly id: string; readonly reference: string; readonly typeCode: string; readonly workPackageId: string | null; readonly validFrom: string; readonly validUntil: string; readonly location: string }>;
}

export interface DeliveryOptionsResponse {
  readonly deliveries: ReadonlyArray<{ readonly id: string; readonly reference: string; readonly courierCompany: string; readonly packageCount: number; readonly expectedOn: string | null; readonly arrived: boolean; readonly handoverArea: string | null }>;
  readonly vehicles: ReadonlyArray<{ readonly id: string; readonly plate: string }>;
}

export interface PeopleOptionsResponse {
  readonly items: readonly PersonRef[];
}
